import { hexToRgb, hsvToRgb, rgbToHex, rgbToHsv } from '@utilities/functions';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

interface GradientControllerProps {
  defaultType: 'linear' | 'radial';
  defaultColors: string[];
  defaultPositions: number[];
  defaultAngle: number;
  onChange?: (gradient: {
    colors: string[];
    positions: number[];
    type: 'linear' | 'radial';
    angle?: number;
  }) => void;
}

export const useGradientPickerController = ({
  defaultType,
  defaultColors,
  defaultPositions,
  defaultAngle,
  onChange,
}: GradientControllerProps) => {
  // Core state
  const [gradientType, setGradientType] = useState<'linear' | 'radial'>(defaultType);
  const [colors, setColors] = useState<string[]>(defaultColors);
  const [positions, setPositions] = useState<number[]>(defaultPositions);
  const [angle, setAngle] = useState<number>(defaultAngle);
  const [currentColorIndex, setCurrentColorIndex] = useState<number | null>(0);
  const [isDraggingAngle, setIsDraggingAngle] = useState(false);
  const [isDraggingPicker, setIsDraggingPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ x: 50, y: 50 });
  const [currentHue, setCurrentHue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Refs
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const spectrumRef = useRef<HTMLDivElement | null>(null);

  // spectrum hue
  const updateHue = useCallback((clientX: number) => {
    if (!spectrumRef.current) return;
    const rect = spectrumRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    const hue = (x / rect.width) * 360;
    setCurrentHue(hue);
  }, []);

  const handleSpectrumMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    updateHue(e.clientX);
  }, [updateHue]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateHue(e.clientX);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, updateHue]);


  // RGB values for the current color
  const [rgbaValues, setRgbaValues] = useState<{ r: number; g: number; b: number; a: number }>({ r: 0, g: 0, b: 0, a: 100 });

  // Color presets
  const colorPresets = [
    ['#11998e', '#38ef7d'], // Green Beach
    ['#8E2DE2', '#4A00E0'], // Purple Love
    ['#f12711', '#f5af19'], // Witching Hour
    ['#12c2e9', '#c471ed', '#f64f59'], // Subtle
    ['#667eea', '#764ba2'], // Plum Plate
    ['#00F5A0', '#00D9F5'], // Aqua Splash
    '#22c1c3', // Teal
    '#4a00e0', // Deep Purple
    '#f5af19', // Gold
    '#f64f59', // Coral
    '#00d9f5', // Cyan
    '#ff9cee'  // Pink
  ];

  // Update RGBA values and HSV when current color changes
  useEffect(() => {
    if (currentColorIndex !== null && colors[currentColorIndex]) {
      const rgba = hexToRgb(colors[currentColorIndex]);
      setRgbaValues(rgba);

      // Calculate HSV
      const { h, s, v } = rgbToHsv(rgba.r, rgba.g, rgba.b);
      setCurrentHue(h);

      // Update picker position based on saturation and value
      setPickerPosition({
        x: Math.round(s * 100),
        y: Math.round((1 - v) * 100)
      });
    }
  }, [currentColorIndex, colors]);

  // Calculate gradient string for preview
  const gradientString = useMemo(() => {
    const sortedStops = colors.map((color, index) => ({
      color,
      position: positions[index],
    })).sort((a, b) => a.position - b.position);

    const colorStops = sortedStops
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(', ');

    if (gradientType === 'linear') {
      return `linear-gradient(${angle}deg, ${colorStops})`;
    } else {
      return `radial-gradient(circle, ${colorStops})`;
    }
  }, [colors, positions, gradientType, angle]);

  // Call onChange when gradient changes
  useEffect(() => {
    if (onChange) {
      onChange({
        colors,
        positions,
        type: gradientType,
        angle: gradientType === 'linear' ? angle : undefined,
      });
    }
  }, [colors, positions, gradientType, angle, onChange]);

  // Handlers for color stops
  const addColorStop = (color = '#ffffff', position = 50) => {
    const newColors = [...colors, color];
    const newPositions = [...positions, position];
    setColors(newColors);
    setPositions(newPositions);
    setCurrentColorIndex(newColors.length - 1);
  };

  const removeColorStop = (index: number) => {
    if (colors.length <= 2) return; // Keep at least 2 colors

    const newColors = colors.filter((_, i) => i !== index);
    const newPositions = positions.filter((_, i) => i !== index);

    setColors(newColors);
    setPositions(newPositions);

    if (currentColorIndex === index) {
      setCurrentColorIndex(Math.min(currentColorIndex, newColors.length - 1));
    } else if (currentColorIndex !== null && currentColorIndex > index) {
      setCurrentColorIndex(currentColorIndex - 1);
    }
  };

  const updateColorStop = (index: number | null, color: string) => {
    if (index === null) return;
    const newColors = [...colors];
    newColors[index] = color;
    setColors(newColors);
  };

  const updatePosition = (index: number | null, position: number) => {
    if (index === null) return;
    const newPositions = [...positions];
    newPositions[index] = Math.max(0, Math.min(100, position));
    setPositions(newPositions);
  };

  // Update RGB values and the color
  const updateRgba = (field: 'r' | 'g' | 'b' | 'a', value: number) => {
    if (currentColorIndex === null) return;

    const newRgba = { ...rgbaValues, [field]: value };
    setRgbaValues(newRgba);

    const newHex = rgbToHex(newRgba.r, newRgba.g, newRgba.b, newRgba.a);
    updateColorStop(currentColorIndex, newHex);

    // Update HSV and picker position if not dragging
    if (!isDraggingPicker) {
      const { h, s, v } = rgbToHsv(newRgba.r, newRgba.g, newRgba.b);
      setCurrentHue(h);
      setPickerPosition({
        x: Math.round(s * 100),
        y: Math.round((1 - v) * 100)
      });
    }
  };

  // Angle control handlers
  const handleAngleCircleMouseDown = () => {
    setIsDraggingAngle(true);
  };

  // Color picker handlers
  const handleColorPickerMouseDown = (e: React.MouseEvent) => {
    if (!colorPickerRef.current || currentColorIndex === null) return;

    setIsDraggingPicker(true);
    updatePickerPosition(e);
  };

  const updatePickerPosition = (e: React.MouseEvent | MouseEvent) => {
    if (!colorPickerRef.current || currentColorIndex === null) return;

    const rect = colorPickerRef.current.getBoundingClientRect();

    // Calculate position as percentage
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));

    setPickerPosition({ x, y });

    // Convert position to HSV
    const s = x / 100;
    const v = 1 - (y / 100);

    // Convert HSV to RGB using current hue
    const rgb = hsvToRgb(currentHue, s, v);

    // Update RGBA values and color
    const newRgba = { ...rgbaValues, r: rgb.r, g: rgb.g, b: rgb.b };
    setRgbaValues(newRgba);

    const newColor = rgbToHex(rgb.r, rgb.g, rgb.b, rgbaValues.a);
    updateColorStop(currentColorIndex, newColor);
  };

  // Handle mouse events for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingAngle) {
        e.preventDefault();
        const circleElement = document.getElementById('angle-circle');
        if (!circleElement) return;

        const rect = circleElement.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = e.clientX - centerX;
        const deltaY = e.clientY - centerY;

        // Calculate angle in degrees
        let newAngle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
        if (newAngle < 0) newAngle += 360;

        setAngle(Math.round(newAngle));
      } else if (isDraggingPicker) {
        updatePickerPosition(e);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingAngle(false);
      setIsDraggingPicker(false);
    };

    if (isDraggingAngle || isDraggingPicker) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingAngle, isDraggingPicker]);

  return {
    gradientType,
    colors,
    positions,
    angle,
    rgbaValues,
    currentColorIndex,
    colorPickerRef,
    spectrumRef,
    gradientString,
    colorPresets,
    currentHue,
    pickerPosition,
    setAngle,
    setColors,
    updateRgba,
    setPositions,
    addColorStop,
    updatePosition,
    setGradientType,
    removeColorStop,
    updateColorStop,
    setCurrentColorIndex,
    handleSpectrumMouseDown,
    handleAngleCircleMouseDown,
    handleColorPickerMouseDown
  };
};